import { SidePanelNewSectionTemplate } from "components/MainSectionTemplate/SidePanelSection/SidePanelNewSectionTemplate";
import { CheckboxList } from "components/CheckboxList";
import CountryDropdown from "components/dropdowns/reactselect/CountryDropdown";
import StateDropdown from "components/dropdowns/reactselect/StateDropdown";
import { StatusDropdown } from "components";
import UrlInput from "components/urlinput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import React, { useCallback } from "react";
import { findCountryById, findStateById } from "shared/utils/countrystateutils";
import CompanyNameField from "./CompanyNameField";
import { useCustomQuery } from "utilities/hookutils";
import { useDebounce } from "utilities/usehooks";
import { Message } from "semantic-ui-react";
import { PhoneInput } from "components/PhoneInput";
import { getCountryFormDefinition } from "shared/utils/cmsUtils";
import { useOpenSidePanel } from "../SidePanel";
import { useOpenCompanySidePanel } from "./CompanySidePanel";
import TagsSelect from "components/Tag/TagsSelect";
import { AfterCreateAction } from "../types";
import { useClientId } from "common/SessionContext";
import { getCreateCompanyInstruction, getInitialNewCompanyFormState, NewCompanyFormState } from "shared/utils/fieldsHelper/newCompanyFields";

type NewCompanyType = "artist" | "venue" | "promoter" | "company";

interface Props {
  type: NewCompanyType;
  disableArtist?: boolean;
  disableVenue?: boolean;
  disablePromoter?: boolean;
  onCreated: (company: server.dto.Company) => void;
  afterCreate: AfterCreateAction;
  statuses: server.dto.StatusSmall[];
}

const NewCompanySidePanel = (props: Props) => {
  const [formState, setFormState] = React.useState<NewCompanyFormState>(getInitialNewCompanyFormState(serverReference, site.userPreference, props.statuses, props.type));
  const openCompanySidePanel = useOpenCompanySidePanel();
  const clientId = useClientId();
  const onChange = (newData: Partial<NewCompanyFormState>) => {
    setFormState((prevState) => ({
      ...prevState,
      ...newData,
    }));
  }

  const onGooglePlaceSelected = (place: server.dto.GooglePlace) => {
    onChange({
      companyName: place.name ?? "",
      addressLine1: place.address.addressLine1 ?? "",
      addressLine2: place.address.addressLine2 ?? "",
      postalCode: place.address.postalCode ?? "",
      city: place.address.city ?? "",
      country: findCountryById(place.address.country, serverReference) ?? undefined,
      state: findStateById(place.address.stateId, serverReference) ?? undefined,
      website: truncateUrl(place.website ?? ""),
      googlePlaceId: place.placeIdentifier,
      latLng: place.latLng
    });
  }

  const onNorwegianCompanySelected = (data: server.dto.NorwegianCompanyRegistration) => {
    onChange({
      companyName: data.name ?? "",
      addressLine1: data.address?.addressLine1 ?? "",
      addressLine2: data.address?.addressLine2 ?? "",
      postalCode: data.address?.postalCode ?? "",
      city: data.address?.city ?? "",
      country: data.address ? findCountryById(data.address.country, serverReference) : findCountryById("norway", serverReference),
      state: data.address ? findStateById(data.address.stateId, serverReference) : findCountryById("norway", serverReference).states.find(s => s.isCountryDefault),
      website: truncateUrl(data.website ?? ""),
    })
  }

  const onSuccess = async (companyId: number) => {
    const res = await window.CoreApi.Company.GetSidePanelData(companyId).then(r => r.data);
    props.onCreated(res.data.company);
  }

  const submit = () => {
    return window.CoreApi.Company.Create(getCreateCompanyInstruction(formState, serverReference));
  };

  const debouncedCompanyName = useDebounce(formState.companyName, 500);
  const companyExistsQuery = useCustomQuery({
    queryKey: ["companyExists", debouncedCompanyName],
    queryFn: () => window.CoreApi.Company.NameExists({ name: debouncedCompanyName }).then(r => r.data),
    enabled: !!debouncedCompanyName,
  });

  const formDefinition = getCountryFormDefinition(formState.country.key, serverReference);

  return <SidePanelNewSectionTemplate
    onSave={(id) => {
      onSuccess(id);
      if (props.afterCreate === "displayItemInSidebar") {
        openCompanySidePanel(id, props.type);
      }
    }}
    afterCreate={props.afterCreate}
    title={site.currentEnvironment.company.newLabel}
    id="new-company-side-panel"
    trigger={null}
    submit={submit}
    sections={[
      {
        key: "companyExistsWarning",
        type: "custom",
        hidden: !companyExistsQuery.data?.data,
        render: () => <Message color="red" className="margin-top margin-bottom">
          {RESX.Company.ACompanyWithThisNameAlreadyExists}
        </Message>
      },
      {
        key: "companyMainDetails",
        type: "regular",
        hideTitle: true,
        title: RESX.Company.resxCompany,
        fields: [
          {
            key: "name",
            label: RESX.GeneralLabels.Name,
            render: () => (
              <CompanyNameField
                country={formState.country}
                name={formState.companyName}
                onChange={(newName) => onChange({ companyName: newName })}
                onGooglePlaceSelected={onGooglePlaceSelected}
                onNorwegianCompanySelected={onNorwegianCompanySelected}
              />
            )
          }, {
            key: "status",
            label: RESX.Status.resxStatus,
            hidden: props.statuses.length <= 1,
            render: () => (
              <StatusDropdown
                mainArea="company"
                clientId={clientId}
                value={formState.status}
                options={undefined}
                onChange={(value) => onChange({ status: value })}
              />
            )
          }, {
            key: "profile",
            label: RESX.Company.Profile,
            render: () => (
              <ProfileCheckboxList
                isArtist={formState.isArtist}
                isVenue={formState.isVenue}
                isPromoter={formState.isPromoter}
                disableArtist={props.disableArtist}
                disableVenue={props.disableVenue}
                disablePromoter={props.disablePromoter}
                onChange={onChange}
              />
            )
          }, {
            key: "tags",
            label: RESX.Tag.Tags,
            render: () => <TagsSelect
              mainArea="company"
              value={formState.tags}
              onChange={(tags) => onChange({ tags })}
              inPageEditing="new"
            />
          }
        ]
      },
      {
        key: "address",
        type: "regular",
        title: RESX.Address.resxAddress,
        fields: [
          {
            key: "addressLine1",
            label: RESX.Address.AddressLine + " 1",
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxAddressLineLength}
                value={formState.addressLine1}
                onChange={(value) => onChange({ addressLine1: value })}
              />
            )
          }, {
            key: "addressLine2",
            label: RESX.Address.AddressLine + " 2",
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxAddressLineLength}
                value={formState.addressLine2}
                onChange={(value) => onChange({ addressLine2: value })}
              />
            )
          }, {
            key: "postalCode",
            label: RESX.Address.PostalCode,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxPostalCodeLength}
                value={formState.postalCode}
                onChange={(value) => onChange({ postalCode: value })}
              />
            )
          }, {
            key: "city",
            label: RESX.Address.City,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.address.maxCityLength}
                value={formState.city}
                onChange={(value) => onChange({ city: value })}
              />
            )
          }, {
            key: "country",
            label: RESX.Address.Country,
            render: () => (
              <CountryDropdown
                value={formState.country}
                onChange={(value) => onChange({ country: value, state: value.states.find(s => s.isCountryDefault) })}
              />
            )
          }, {
            key: "state",
            label: RESX.Address.State,
            render: () => (
              <StateDropdown
                value={formState.state}
                options={formState.country?.states ?? []}
                onChange={(value) => onChange({ state: value })}
              />
            )
          }
        ]
      },
      {
        key: "companyFinancialDetails",
        type: "regular",
        title: RESX.Financial.FinancialDetails,
        fields: [
          {
            key: "euVatNumber",
            label: formDefinition.euVatNumber.label,
            sublabel: formDefinition.euVatNumber.sublabel,
            hidden: !formDefinition.euVatNumber.displayInUI,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.company.maxEuVatNumberLength}
                value={formState.euVatNumber}
                onChange={(value) => onChange({ euVatNumber: value })}
              />
            )
          }, {
            key: "fiscalNumber1",
            label: formDefinition.fiscalNumber1.label,
            sublabel: formDefinition.fiscalNumber1.sublabel,
            hidden: !formDefinition.fiscalNumber1.displayInUI,
            render: () => (
              <OptionalTextInput
                maxLength={serverReference.constants.company.maxFiscalNumber1Length}
                value={formState.fiscalNumber1}
                onChange={(value) => onChange({ fiscalNumber1: value })}
              />
            )
          }
        ]
      },
      {
        key: "contactDetailSection",
        type: "regular",
        title: RESX.Contact.ContactDetails,
        fields: [
          {
            key: "phoneNumber",
            label: RESX.ContactDetails.PhoneNumber,
            render: () => (
              <PhoneInput
                value={formState.phoneNumber}
                onChange={(value) => onChange({ phoneNumber: value })}
              />
            )
          }, {
            key: "website",
            label: RESX.GeneralLabels.Website,
            render: () => (
              <UrlInput
                validatorKey="website"
                value={formState.website}
                onValueChange={(value) => onChange({ website: value })}
                maxLength={serverReference.constants.url.maxUrlLength}
              />
            )
          }
        ]
      },
      {
        key: "companyVenueDetails",
        type: "regular",
        title: site.currentEnvironment.functionGroups.venue.singular,
        fields: [
          {
            key: "capacity",
            label: RESX.Venue.Capacity,
            hidden: !formState.isVenue,
            render: () => (
              <OptionalNumberInput
                value={formState.capacity}
                maxValue={serverReference.constants.venue.maxCapacity}
                labelContent="PAX"
                onChange={(value) => onChange({ capacity: value })}
                onEnterPressed={value => onChange({ capacity: value })}
                decimalPoints={0}
                canBeEmpty
                canBeNegative={false}
                width={100}
              />
            )
          }
        ]
      }
    ]}
  />
}

type ProfileKey = "artist" | "venue" | "promoter";

interface ProfileCheckboxListProps {
  isArtist: boolean;
  isVenue: boolean;
  isPromoter: boolean;
  disableArtist?: boolean;
  disableVenue?: boolean;
  disablePromoter?: boolean;
  onChange: (value: { isArtist: boolean; isVenue: boolean; isPromoter: boolean }) => void;
}

const ProfileCheckboxList = (props: ProfileCheckboxListProps) => {
  const fg = site.currentEnvironment.functionGroups;

  const options: { key: ProfileKey; label: string; disabled?: boolean }[] = [];
  if (fg.artist.enabled) options.push({ key: "artist", label: fg.artist.singular, disabled: props.disableArtist });
  if (fg.venue.enabled) options.push({ key: "venue", label: fg.venue.singular, disabled: props.disableVenue });
  if (fg.promoter.enabled) options.push({ key: "promoter", label: fg.promoter.singular, disabled: props.disablePromoter });

  const selectedKeys = new Set<ProfileKey>();
  if (props.isArtist) selectedKeys.add("artist");
  if (props.isVenue) selectedKeys.add("venue");
  if (props.isPromoter) selectedKeys.add("promoter");
  const value = options.filter(o => selectedKeys.has(o.key));

  return <CheckboxList
    options={options}
    getId={o => o.key}
    getLabel={o => o.label}
    getDisabled={o => o.disabled}
    value={value}
    onChange={(selected) => {
      const keys = new Set(selected.map(s => s.key));
      props.onChange({
        isArtist: keys.has("artist"),
        isVenue: keys.has("venue"),
        isPromoter: keys.has("promoter"),
      });
    }}
    multipleColumns={false}
    inPageEditing="new"
    keyboardNavigationGroup="newCompanyProfile"
  />;
};

export const useOpenNewCompanySidePanel = () => {
  const openSidePanel = useOpenSidePanel();
  const clientId = useClientId();

  const openNewCompanySidePanel = useCallback(async (props: Omit<Props, "statuses">) => {
    const res = await window.CoreApi.Status.ListSmall({ clientId, mainArea: "company" });
    openSidePanel(
      <NewCompanySidePanel {...props} statuses={res.data.data} />,
      "new-company-side-panel",
      "large"
    );
  }, [openSidePanel, clientId]);

  return openNewCompanySidePanel;
}

const truncateUrl = (input: string) => {
  if (input == null) return "";

  const maxLength = serverReference.constants.url.maxUrlLength;
  // If the URL is short enough, return it as is
  if (input.length <= maxLength) return input;

  try {
    const url = new URL(input);

    // First remove the query params, see if it fits within the max length
    const withPath = url.origin + url.pathname;
    if (withPath.length <= maxLength) {
      return withPath;
    }

    // Otherwise remove the path & query params, just get the origin like "https://www.example.com"    
    return url.origin;
  } catch {
    // If it's not a valid URL, just return an empty string
    return "";
  }
}
